import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Activity, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { api } from "../../lib/api";

/**
 * LiveEventTrail — polls `/api/orders/{id}/events` and renders the persistent
 * pipeline event trail so a stuck order can be diagnosed WITHOUT supervisor
 * log access. Auto-expands with a warning banner when the last event is >90s
 * old and the order isn't in a terminal state (complete / failed / sent_to_print).
 *
 * Poll cadence: 4s while the panel is open OR the order looks stuck. Stops
 * polling once the order reaches a terminal state.
 */
const TERMINAL_STATUSES = new Set(["complete", "sent_to_print", "shipped", "failed"]);
const STUCK_THRESHOLD_MS = 90_000; // 90s without a new event → assume stuck

export function LiveEventTrail({ order }) {
    const [open, setOpen] = React.useState(false);
    const [events, setEvents] = React.useState([]);
    const [lastFetched, setLastFetched] = React.useState(null);
    const [autoExpanded, setAutoExpanded] = React.useState(false);

    const orderId = order?.id;
    const isTerminal = TERMINAL_STATUSES.has(order?.status);

    const fetchEvents = React.useCallback(async () => {
        if (!orderId) return;
        try {
            const { data } = await api.get(`/orders/${orderId}/events`);
            setEvents(data.events || []);
            setLastFetched(new Date());
        } catch (e) {
            // Silent — this panel is diagnostic-only, don't spam the user.
        }
    }, [orderId]);

    // Poll while open OR while order isn't in a terminal state.
    React.useEffect(() => {
        if (!orderId) return undefined;
        if (isTerminal && !open) return undefined; // fully done, stop polling
        fetchEvents(); // initial hit
        const id = setInterval(fetchEvents, 4000);
        return () => clearInterval(id);
    }, [orderId, open, isTerminal, fetchEvents]);

    // Compute "looks stuck": last event >90s old AND status is not terminal.
    const lastEvent = events[events.length - 1];
    const lastEventAgeMs = lastEvent?.ts
        ? Date.now() - new Date(lastEvent.ts).getTime()
        : null;
    const looksStuck = !isTerminal && lastEventAgeMs != null && lastEventAgeMs > STUCK_THRESHOLD_MS;

    // Auto-expand the panel once (per session) when we first detect a stall so
    // the parent immediately sees WHY their book is spinning.
    React.useEffect(() => {
        if (looksStuck && !autoExpanded) {
            setOpen(true);
            setAutoExpanded(true);
        }
    }, [looksStuck, autoExpanded]);

    if (!orderId) return null;
    // Hide entirely for terminal orders with zero events (nothing to show).
    if (isTerminal && events.length === 0) return null;

    return (
        <div
            data-testid="sf-live-event-trail"
            className={`paper-card rounded-2xl px-5 py-4 mb-6 border ${looksStuck ? "border-amber-400/60 bg-amber-50/40" : "border-border/60"}`}
        >
            <button
                type="button"
                data-testid="sf-live-event-trail-toggle"
                onClick={() => setOpen((o) => !o)}
                className="w-full flex items-center gap-3 text-left"
            >
                {looksStuck ? (
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                ) : (
                    <Activity className="w-4 h-4 text-muted-foreground shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                        {looksStuck ? "Pipeline stalled — live event trail" : "Live pipeline events"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate">
                        {events.length === 0 ? (
                            "No events yet — waiting for the pipeline to start emitting."
                        ) : (
                            <>
                                Last step:{" "}
                                <span className="font-mono">{lastEvent?.event}</span>
                                {lastEventAgeMs != null && (
                                    <> · {formatAge(lastEventAgeMs)}</>
                                )}
                                {" · "}
                                {events.length} event{events.length === 1 ? "" : "s"}
                            </>
                        )}
                    </p>
                </div>
                {open ? (
                    <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" />
                ) : (
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                )}
            </button>
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: "easeOut" }}
                        className="overflow-hidden"
                    >
                        <div
                            data-testid="sf-live-event-trail-list"
                            className="mt-4 max-h-72 overflow-y-auto rounded-lg border border-border/60 bg-background/60 divide-y divide-border/40"
                        >
                            {events.length === 0 ? (
                                <div className="px-3 py-4 text-xs text-muted-foreground italic">
                                    No events recorded yet for this order.
                                </div>
                            ) : (
                                events.map((ev, i) => <EventRow key={i} ev={ev} />)
                            )}
                        </div>
                        <p className="mt-3 text-[11px] text-muted-foreground">
                            Trail auto-refreshes every 4s and expires after 7 days.
                            {lastFetched && (
                                <> · Fetched {lastFetched.toLocaleTimeString()}</>
                            )}
                        </p>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function EventRow({ ev }) {
    const ts = ev.ts ? new Date(ev.ts) : null;
    const meta = ev.meta && Object.keys(ev.meta).length > 0 ? ev.meta : null;
    const isError = ev.event.includes("exception") || ev.event.includes("failed") || ev.event.includes("error");
    return (
        <div
            data-testid="sf-live-event-trail-row"
            className={`px-3 py-2 flex items-start gap-3 text-xs ${isError ? "bg-red-50/50" : ""}`}
        >
            <span className="font-mono text-[10px] text-muted-foreground tabular-nums shrink-0 w-16">
                {ts ? ts.toLocaleTimeString("en-US", { hour12: false }) : "--"}
            </span>
            <div className="flex-1 min-w-0">
                <p className={`font-mono ${isError ? "text-red-700 font-semibold" : "text-foreground"}`}>
                    {ev.event}
                </p>
                {meta && (
                    <p className="mt-1 font-mono text-[10px] text-muted-foreground break-all whitespace-pre-wrap">
                        {JSON.stringify(meta)}
                    </p>
                )}
            </div>
        </div>
    );
}

function formatAge(ms) {
    if (ms < 1000) return "just now";
    const s = Math.round(ms / 1000);
    if (s < 60) return `${s}s ago`;
    const m = Math.round(s / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60);
    return `${h}h ago`;
}
